/**
 * SubmissionLinkSettings — Manage investor's public submission link
 *
 * 3 states:
 *   1. No link exists     -> creation CTA
 *   2. Link active        -> details card with edit/share/copy/toggle/delete
 *   3. Link paused        -> details with paused banner + reactivate
 *
 * Wired to real Supabase hooks from @/lib/supabase/submissions.ts
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  TextInput,
  Share,
  ActivityIndicator,
} from 'react-native';
import * as Clipboard from 'expo-clipboard';
import {
  Inbox,
  Copy,
  ExternalLink,
  Pencil,
  MoreHorizontal,
  Trash2,
  Pause,
  Play,
  Check,
  AlertTriangle,
  Link as LinkIcon,
} from 'lucide-react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  FadeIn,
  FadeOut,
  SlideInDown,
} from 'react-native-reanimated';
import { useRouter } from 'expo-router';
import { useThemeColors } from '@/lib/useThemeColors';
import { useProfileStore } from '@/lib/state/profile-store';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useAlert } from '@/components/AlertDialog';
import { hapticLight, hapticSuccess, hapticWarning, hapticMedium } from '@/lib/haptics';
import { handleError, getUserMessage } from '@/lib/errors';
import {
  useSubmissionLinks,
  useCreateSubmissionLink,
  useUpdateSubmissionLink,
  useDeleteSubmissionLink,
} from '@/lib/supabase/submissions';
import type { SubmissionLink } from '@/lib/supabase/submissions';

const BASE_URL = 'inverio.ai/submit';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function validateSlug(slug: string): string | null {
  if (slug.length < 3) return 'Slug must be at least 3 characters';
  if (slug.length > 50) return 'Slug must be 50 characters or fewer';
  if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug))
    return 'Only lowercase letters, numbers, and hyphens allowed';
  return null;
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function formatStorage(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${Math.round(mb)} MB`;
}

function formatMaxStorage(maxMb: number): string {
  return maxMb >= 1024 ? `${(maxMb / 1024).toFixed(0)} GB` : `${maxMb} MB`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StorageBar({
  currentBytes,
  maxMb,
  colors,
}: {
  currentBytes: number;
  maxMb: number;
  colors: ReturnType<typeof useThemeColors>;
}) {
  const maxBytes = maxMb * 1024 * 1024;
  const ratio = Math.min(currentBytes / maxBytes, 1);
  const width = useSharedValue(0);

  useEffect(() => {
    width.value = withTiming(ratio, { duration: 800 });
  }, [ratio]);

  const barStyle = useAnimatedStyle(() => ({
    width: `${width.value * 100}%` as unknown as number,
    height: 4,
    borderRadius: 2,
    backgroundColor: ratio > 0.9 ? colors.error : colors.primary[600],
  }));

  return (
    <View className="mt-3">
      <View className="flex-row items-center justify-between mb-1.5">
        <Text style={{ color: colors.text.tertiary }} className="text-xs">
          Storage
        </Text>
        <Text style={{ color: colors.text.tertiary }} className="text-xs">
          {formatStorage(currentBytes)} / {formatMaxStorage(maxMb)}
        </Text>
      </View>
      <View
        className="h-1 rounded-full overflow-hidden"
        style={{ backgroundColor: colors.light[300] }}
      >
        <Animated.View style={barStyle} />
      </View>
    </View>
  );
}

function OverflowMenu({
  onDelete,
  colors,
}: {
  onDelete: () => void;
  colors: ReturnType<typeof useThemeColors>;
}) {
  const [open, setOpen] = useState(false);

  return (
    <View>
      <AnimatedPressable
        onPress={() => {
          hapticLight();
          setOpen((v) => !v);
        }}
        className="w-8 h-8 rounded-lg items-center justify-center"
        style={{ backgroundColor: colors.light[300] }}
        accessibilityRole="button"
        accessibilityLabel="More options"
      >
        <MoreHorizontal size={16} color={colors.text.secondary} />
      </AnimatedPressable>

      {open && (
        <Animated.View
          entering={FadeIn.duration(150)}
          exiting={FadeOut.duration(100)}
          className="absolute top-10 right-0 rounded-xl overflow-hidden z-50"
          style={{
            backgroundColor: colors.surface.primary,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.15,
            shadowRadius: 12,
            elevation: 8,
            minWidth: 180,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}
        >
          <AnimatedPressable
            onPress={() => {
              setOpen(false);
              onDelete();
            }}
            className="flex-row items-center gap-3 px-4 py-3"
            accessibilityRole="button"
            accessibilityLabel="Delete submission link"
          >
            <Trash2 size={16} color={colors.error} />
            <Text style={{ color: colors.error }} className="font-medium">
              Delete Link
            </Text>
          </AnimatedPressable>
        </Animated.View>
      )}
    </View>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function SubmissionLinkSettings() {
  const router = useRouter();
  const colors = useThemeColors();
  const user = useProfileStore((s) => s.user);
  const { confirmDestructive, error: showError } = useAlert();

  // ---------------------------------------------------------------------------
  // Real data from Supabase via React Query
  // ---------------------------------------------------------------------------
  const { data: links = [], isLoading: isLoadingLinks } = useSubmissionLinks(user?.id);
  const link: SubmissionLink | null = links[0] ?? null;

  const createMutation = useCreateSubmissionLink();
  const updateMutation = useUpdateSubmissionLink();
  const deleteMutation = useDeleteSubmissionLink();

  // Any mutation in progress
  const isMutating = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  // UI state
  const [mode, setMode] = useState<'view' | 'create' | 'edit'>('view');
  const [slug, setSlug] = useState('');
  const [thesisMessage, setThesisMessage] = useState('');
  const [slugError, setSlugError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const copyTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Derived
  const fullUrl = useMemo(() => (link ? `https://${BASE_URL}/${link.slug}` : ''), [link?.slug]);
  const displayUrl = useMemo(() => (link ? `${BASE_URL}/${link.slug}` : ''), [link?.slug]);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const openCreate = useCallback(() => {
    const defaultSlug = slugify(user?.name || 'my-link');
    setSlug(defaultSlug);
    setThesisMessage('');
    setSlugError(null);
    setMode('create');
    hapticLight();
  }, [user?.name]);

  const openEdit = useCallback(() => {
    if (!link) return;
    setSlug(link.slug);
    setThesisMessage(link.thesis_message || '');
    setSlugError(null);
    setMode('edit');
    hapticLight();
  }, [link]);

  const cancel = useCallback(() => {
    setMode('view');
    setSlugError(null);
  }, []);

  const handleSlugChange = useCallback((text: string) => {
    const cleaned = text.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSlug(cleaned);
    if (slugError) setSlugError(validateSlug(cleaned));
  }, [slugError]);

  const handleCreate = useCallback(async () => {
    const error = validateSlug(slug);
    if (error) {
      setSlugError(error);
      hapticWarning();
      return;
    }
    if (!user?.id) return;

    try {
      await createMutation.mutateAsync({
        investorId: user.id,
        slug,
        thesisMessage: thesisMessage || undefined,
      });
      setMode('view');
      hapticSuccess();
    } catch (err) {
      const { message, isCancelled } = handleError(err, {
        context: 'SubmissionLinkSettings.handleCreate',
      });
      if (!isCancelled) {
        // Surface slug conflicts inline, everything else as alert
        const msg = err instanceof Error ? err.message : '';
        if (msg.toLowerCase().includes('slug') || msg.toLowerCase().includes('already')) {
          setSlugError(msg);
        } else {
          showError('Link Creation Failed', message);
        }
        hapticWarning();
      }
    }
  }, [slug, thesisMessage, user?.id, createMutation, showError]);

  const handleSave = useCallback(async () => {
    const error = validateSlug(slug);
    if (error) {
      setSlugError(error);
      hapticWarning();
      return;
    }
    if (!link || !user?.id) return;

    try {
      await updateMutation.mutateAsync({
        investorId: user.id,
        linkId: link.id,
        slug,
        thesisMessage: thesisMessage || undefined,
      });
      setMode('view');
      hapticSuccess();
    } catch (err) {
      const { message, isCancelled } = handleError(err, {
        context: 'SubmissionLinkSettings.handleSave',
      });
      if (!isCancelled) {
        const msg = err instanceof Error ? err.message : '';
        if (msg.toLowerCase().includes('slug') || msg.toLowerCase().includes('already')) {
          setSlugError(msg);
        } else {
          showError('Update Failed', message);
        }
        hapticWarning();
      }
    }
  }, [slug, thesisMessage, link, user?.id, updateMutation, showError]);

  const handleToggle = useCallback(async () => {
    if (!link || !user?.id) return;

    try {
      await updateMutation.mutateAsync({
        investorId: user.id,
        linkId: link.id,
        isActive: !link.is_active,
      });
      link.is_active ? hapticWarning() : hapticSuccess();
    } catch (err) {
      const { message, isCancelled } = handleError(err, {
        context: 'SubmissionLinkSettings.handleToggle',
      });
      if (!isCancelled) {
        showError('Toggle Failed', message);
        hapticWarning();
      }
    }
  }, [link, user?.id, updateMutation, showError]);

  const handleDelete = useCallback(async () => {
    if (!link || !user?.id) return;

    const confirmed = await confirmDestructive(
      'Delete Submission Link',
      'This will permanently remove your public link. Startups will no longer be able to submit decks to you.',
      'Delete',
    );
    if (!confirmed) return;

    try {
      await deleteMutation.mutateAsync({
        investorId: user.id,
        linkId: link.id,
      });
      setMode('view');
      hapticMedium();
    } catch (err) {
      const { message, isCancelled } = handleError(err, {
        context: 'SubmissionLinkSettings.handleDelete',
      });
      if (!isCancelled) {
        showError('Delete Failed', message);
        hapticWarning();
      }
    }
  }, [link, user?.id, confirmDestructive, deleteMutation, showError]);

  const handleCopy = useCallback(async () => {
    if (!link) return;
    try {
      await Clipboard.setStringAsync(fullUrl);
      setCopied(true);
      hapticSuccess();
      if (copyTimeout.current) clearTimeout(copyTimeout.current);
      copyTimeout.current = setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      handleError(err, { context: 'SubmissionLinkSettings.handleCopy' });
    }
  }, [fullUrl, link]);

  const handleShare = useCallback(async () => {
    if (!link) return;
    hapticLight();
    try {
      await Share.share({
        url: fullUrl,
        title: 'Submit Your Pitch Deck',
        message: `Submit your pitch deck for review: ${fullUrl}`,
      });
    } catch (err) {
      handleError(err, {
        context: 'SubmissionLinkSettings.handleShare',
        silent: true,
      });
    }
  }, [fullUrl, link]);

  // Cleanup copy timeout
  useEffect(() => {
    return () => {
      if (copyTimeout.current) clearTimeout(copyTimeout.current);
    };
  }, []);

  // =========================================================================
  // RENDER: Loading state
  // =========================================================================

  if (isLoadingLinks) {
    return (
      <View
        className="rounded-2xl p-5 items-center justify-center"
        style={{
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
          minHeight: 120,
        }}
      >
        <ActivityIndicator size="small" color={colors.primary[600]} />
      </View>
    );
  }

  // =========================================================================
  // RENDER: State 1 — No link, show creation CTA
  // =========================================================================

  if (!link && mode !== 'create') {
    return (
      <View
        className="rounded-2xl p-5"
        style={{
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
      >
        <View className="flex-row items-center gap-3 mb-4">
          <View
            className="w-10 h-10 rounded-xl items-center justify-center"
            style={{ backgroundColor: colors.primary[50] }}
          >
            <Inbox size={20} color={colors.primary[600]} />
          </View>
          <Text
            style={{ color: colors.text.primary }}
            className="text-base font-semibold"
          >
            Submission Link
          </Text>
        </View>

        <Text
          style={{ color: colors.text.secondary }}
          className="text-sm leading-5 mb-5"
        >
          Create a public link where startups can send you their pitch deck. AI
          scores each submission automatically.
        </Text>

        <AnimatedPressable
          onPress={openCreate}
          className="flex-row items-center justify-center gap-2 py-3.5 rounded-xl"
          style={{ backgroundColor: colors.primary[600] }}
          accessibilityRole="button"
          accessibilityLabel="Create submission link"
        >
          <LinkIcon size={18} color="#FFFFFF" />
          <Text className="text-white font-semibold text-sm">
            Create Submission Link
          </Text>
        </AnimatedPressable>
      </View>
    );
  }

  // =========================================================================
  // RENDER: Create / Edit form
  // =========================================================================

  if (mode === 'create' || mode === 'edit') {
    const isFormMutating = mode === 'create' ? createMutation.isPending : updateMutation.isPending;

    return (
      <Animated.View
        entering={SlideInDown.duration(300).springify()}
        style={{
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
          borderRadius: 16,
          padding: 20,
        }}
      >
        <View className="flex-row items-center gap-3 mb-5">
          <View
            className="w-10 h-10 rounded-xl items-center justify-center"
            style={{ backgroundColor: colors.primary[50] }}
          >
            <Inbox size={20} color={colors.primary[600]} />
          </View>
          <Text
            style={{ color: colors.text.primary }}
            className="text-base font-semibold"
          >
            {mode === 'create' ? 'Create Submission Link' : 'Edit Submission Link'}
          </Text>
        </View>

        {/* Slug input */}
        <View className="mb-4">
          <Text
            style={{ color: colors.text.secondary }}
            className="text-sm font-medium mb-1.5"
          >
            Link Slug
          </Text>
          <View
            className="flex-row items-center rounded-xl overflow-hidden"
            style={{
              backgroundColor: colors.surface.secondary,
              borderWidth: slugError ? 1 : 0,
              borderColor: slugError ? colors.error : 'transparent',
            }}
          >
            <View className="px-3 py-3">
              <Text
                style={{ color: colors.text.tertiary }}
                className="text-sm"
              >
                {BASE_URL}/
              </Text>
            </View>
            <TextInput
              value={slug}
              onChangeText={handleSlugChange}
              placeholder="your-slug"
              placeholderTextColor={colors.text.tertiary}
              autoCapitalize="none"
              autoCorrect={false}
              className="flex-1 py-3 pr-3"
              style={{ color: colors.text.primary, fontSize: 15 }}
              accessibilityLabel="Submission link slug"
              accessibilityHint="Enter lowercase letters, numbers, and hyphens. 3 to 50 characters."
            />
          </View>
          {slugError && (
            <Text style={{ color: colors.error }} className="text-xs mt-1.5 ml-1">
              {slugError}
            </Text>
          )}
        </View>

        {/* Thesis message */}
        <View className="mb-5">
          <Text
            style={{ color: colors.text.secondary }}
            className="text-sm font-medium mb-1.5"
          >
            Thesis Message{' '}
            <Text style={{ color: colors.text.tertiary }} className="font-normal">
              (optional)
            </Text>
          </Text>
          <TextInput
            value={thesisMessage}
            onChangeText={(t) => setThesisMessage(t.slice(0, 500))}
            placeholder="Tell startups what you're looking for..."
            placeholderTextColor={colors.text.tertiary}
            multiline
            numberOfLines={3}
            className="rounded-xl px-3 py-3 min-h-[88px]"
            style={{
              backgroundColor: colors.surface.secondary,
              color: colors.text.primary,
              textAlignVertical: 'top',
              fontSize: 15,
            }}
            accessibilityLabel="Thesis message for submitters"
          />
          <Text
            style={{ color: colors.text.tertiary }}
            className="text-xs mt-1 text-right"
          >
            {thesisMessage.length}/500
          </Text>
        </View>

        {/* Action buttons */}
        <View className="flex-row gap-3">
          <AnimatedPressable
            onPress={cancel}
            className="flex-1 py-3 rounded-xl items-center"
            style={{ backgroundColor: colors.light[300] }}
            accessibilityRole="button"
            accessibilityLabel="Cancel"
          >
            <Text
              style={{ color: colors.text.secondary }}
              className="font-semibold text-sm"
            >
              Cancel
            </Text>
          </AnimatedPressable>

          <AnimatedPressable
            onPress={mode === 'create' ? handleCreate : handleSave}
            disabled={isFormMutating}
            className="flex-1 py-3 rounded-xl items-center flex-row justify-center gap-2"
            style={{
              backgroundColor: colors.primary[600],
              opacity: isFormMutating ? 0.7 : 1,
            }}
            accessibilityRole="button"
            accessibilityLabel={mode === 'create' ? 'Create link' : 'Save changes'}
          >
            {isFormMutating ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <Text className="text-white font-semibold text-sm">
                {mode === 'create' ? 'Create' : 'Save'}
              </Text>
            )}
          </AnimatedPressable>
        </View>
      </Animated.View>
    );
  }

  // =========================================================================
  // RENDER: State 2/3 — Active or Paused link details
  // =========================================================================

  if (!link) return null;

  const submissionCount = link.submission_count ?? 0;

  return (
    <View
      className="rounded-2xl overflow-hidden"
      style={{
        backgroundColor: colors.surface.primary,
        borderWidth: 1,
        borderColor: colors.border.light,
      }}
    >
      {/* Paused banner */}
      {!link.is_active && (
        <Animated.View
          entering={FadeIn.duration(200)}
          className="flex-row items-center gap-2 px-4 py-2.5"
          style={{ backgroundColor: colors.amber.light }}
        >
          <AlertTriangle size={14} color={colors.amber.main} />
          <Text style={{ color: colors.amber.main }} className="text-xs font-medium flex-1">
            Your submission link is paused. Startups can't submit new decks.
          </Text>
        </Animated.View>
      )}

      <View className="p-5">
        {/* Header row */}
        <View className="flex-row items-center justify-between mb-4">
          <View className="flex-row items-center gap-3 flex-1">
            <View
              className="w-10 h-10 rounded-xl items-center justify-center"
              style={{
                backgroundColor: link.is_active
                  ? colors.primary[50]
                  : colors.amber.light,
              }}
            >
              <Inbox
                size={20}
                color={link.is_active ? colors.primary[600] : colors.amber.main}
              />
            </View>
            <Text
              style={{ color: colors.text.primary }}
              className="text-base font-semibold"
            >
              Submission Link
            </Text>
          </View>

          <View className="flex-row items-center gap-2">
            <AnimatedPressable
              onPress={openEdit}
              className="w-8 h-8 rounded-lg items-center justify-center"
              style={{ backgroundColor: colors.primary[50] }}
              accessibilityRole="button"
              accessibilityLabel="Edit submission link"
            >
              <Pencil size={14} color={colors.primary[600]} />
            </AnimatedPressable>
            <OverflowMenu onDelete={handleDelete} colors={colors} />
          </View>
        </View>

        {/* Link URL row */}
        <View
          className="flex-row items-center rounded-xl px-3 py-2.5 mb-3"
          style={{ backgroundColor: colors.surface.secondary }}
        >
          <LinkIcon size={14} color={colors.text.tertiary} />
          <Text
            style={{ color: colors.primary[600] }}
            className="text-sm font-medium flex-1 ml-2"
            numberOfLines={1}
          >
            {displayUrl}
          </Text>

          <View className="flex-row items-center gap-1.5 ml-2">
            <AnimatedPressable
              onPress={handleCopy}
              className="flex-row items-center gap-1 px-2.5 py-1.5 rounded-lg"
              style={{
                backgroundColor: copied ? colors.traffic.greenLight : colors.primary[50],
              }}
              accessibilityRole="button"
              accessibilityLabel={copied ? 'Link copied' : 'Copy link'}
            >
              {copied ? (
                <Check size={18} color={colors.success} />
              ) : (
                <Copy size={18} color={colors.primary[600]} />
              )}
              <Text
                style={{ color: copied ? colors.success : colors.primary[600] }}
                className="text-sm font-medium"
              >
                {copied ? 'Copied!' : 'Copy'}
              </Text>
            </AnimatedPressable>

            <AnimatedPressable
              onPress={handleShare}
              className="flex-row items-center gap-1 px-2.5 py-1.5 rounded-lg"
              style={{ backgroundColor: colors.primary[50] }}
              accessibilityRole="button"
              accessibilityLabel="Share submission link"
            >
              <ExternalLink size={18} color={colors.primary[600]} />
              <Text
                style={{ color: colors.primary[600] }}
                className="text-sm font-medium"
              >
                Share
              </Text>
            </AnimatedPressable>
          </View>
        </View>

        {/* Thesis message */}
        {link.thesis_message && (
          <View className="mb-3">
            <Text
              style={{ color: colors.text.secondary }}
              className="text-sm leading-5 italic"
              numberOfLines={3}
            >
              "{link.thesis_message}"
            </Text>
          </View>
        )}

        {/* Stats row */}
        <View className="flex-row items-center gap-4 mb-3">
          <Text style={{ color: colors.text.tertiary }} className="text-xs">
            {submissionCount} submission{submissionCount !== 1 ? 's' : ''}
          </Text>
          <View
            className="w-1 h-1 rounded-full"
            style={{ backgroundColor: colors.text.tertiary }}
          />
          <Text style={{ color: colors.text.tertiary }} className="text-xs">
            {formatStorage(link.current_storage_bytes)} / {formatMaxStorage(link.max_storage_mb)} used
          </Text>
        </View>

        <Text style={{ color: colors.text.tertiary }} className="text-xs mb-3">
          {link.is_active ? 'Active' : 'Created'} since {formatDate(link.created_at)}
        </Text>

        {/* Storage bar */}
        <StorageBar
          currentBytes={link.current_storage_bytes}
          maxMb={link.max_storage_mb}
          colors={colors}
        />

        {/* Active / Paused toggle */}
        <View className="mt-4 pt-4 border-t" style={{ borderColor: colors.border.light }}>
          <AnimatedPressable
            onPress={handleToggle}
            disabled={isMutating}
            className="flex-row items-center justify-between py-2"
            accessibilityRole="switch"
            accessibilityLabel={link.is_active ? 'Pause submission link' : 'Activate submission link'}
            accessibilityState={{ checked: link.is_active }}
          >
            <View className="flex-row items-center gap-3">
              {link.is_active ? (
                <View
                  className="w-8 h-8 rounded-lg items-center justify-center"
                  style={{ backgroundColor: colors.traffic.greenLight }}
                >
                  <Play size={14} color={colors.success} />
                </View>
              ) : (
                <View
                  className="w-8 h-8 rounded-lg items-center justify-center"
                  style={{ backgroundColor: colors.amber.light }}
                >
                  <Pause size={14} color={colors.amber.main} />
                </View>
              )}
              <View>
                <Text
                  style={{ color: colors.text.primary }}
                  className="font-medium text-sm"
                >
                  {link.is_active ? 'Active' : 'Paused'}
                </Text>
                <Text
                  style={{ color: colors.text.tertiary }}
                  className="text-xs"
                >
                  {link.is_active
                    ? 'Startups can submit decks'
                    : 'Tap to reactivate submissions'}
                </Text>
              </View>
            </View>

            {/* Custom toggle */}
            <View
              className="w-12 h-7 rounded-full justify-center"
              style={{
                backgroundColor: link.is_active
                  ? colors.success
                  : colors.light[400],
              }}
            >
              {isMutating ? (
                <ActivityIndicator
                  size="small"
                  color={link.is_active ? '#FFFFFF' : colors.text.tertiary}
                  style={{ marginLeft: link.is_active ? 22 : 4 }}
                />
              ) : (
                <View
                  className="w-5 h-5 rounded-full bg-white"
                  style={{
                    marginLeft: link.is_active ? 24 : 4,
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 1 },
                    shadowOpacity: 0.15,
                    shadowRadius: 2,
                    elevation: 2,
                  }}
                />
              )}
            </View>
          </AnimatedPressable>
        </View>

        {/* View Inbox button */}
        {submissionCount > 0 && (
          <AnimatedPressable
            onPress={() => router.push('/(investor)/submissions')}
            accessibilityRole="button"
            accessibilityLabel="View submission inbox"
            className="mt-4 pt-4 border-t flex-row items-center justify-center gap-2 py-3 rounded-xl"
            style={{
              borderColor: colors.border.light,
              backgroundColor: colors.primary[50],
            }}
          >
            <Inbox size={18} color={colors.primary[600]} />
            <Text style={{ color: colors.primary[600] }} className="font-semibold text-sm">
              View Inbox ({submissionCount})
            </Text>
          </AnimatedPressable>
        )}
      </View>
    </View>
  );
}
